import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { TextInput, SwitchInput } from '@/components/atoms'
import { DeliveryLocationTypeModel } from '@/types'
import { DeliveryLocationTypeFormState, deliveryLocationTypeFieldsConfig } from './deliveryLocationTypeFieldsConfig'
import { DataType } from '@/utils'
import { useEnterSubmit } from '@/hooks/ui'
import { Spinner } from '@/components/ui/spinner'

interface DeliveryLocationTypeDialogProps {
    open: boolean
    mode: 'create' | 'edit' | 'view'
    editDeliveryLocationType: DeliveryLocationTypeModel.DeliveryLocationType | null
    closeDialog: () => void
    onCreate: (data: DeliveryLocationTypeFormState) => Promise<void>
    onUpdate: (id: string, data: Partial<DeliveryLocationTypeFormState>) => Promise<void>
    loading: boolean
    /** Create mode only: the name to start with (e.g. what was typed in an order's delivery area picker). */
    defaultName?: string
}

export const DeliveryLocationTypeDialog = ({
    open,
    mode,
    editDeliveryLocationType,
    closeDialog,
    onCreate,
    onUpdate,
    loading = false,
    defaultName = '',
}: DeliveryLocationTypeDialogProps) => {
    const {
        handleSubmit,
        reset,
        formState: { errors },
        control,
        watch,
    } = useForm<DeliveryLocationTypeFormState>({
        defaultValues: {
            name: '',
            charge: 0,
            status: 'active',
        },
    })

    const formValues = watch()
    const { fields } = deliveryLocationTypeFieldsConfig({ formValues })

    useEffect(() => {
        if ((mode === 'edit' || mode === 'view') && editDeliveryLocationType) {
            reset({
                name: editDeliveryLocationType.name,
                charge: editDeliveryLocationType.charge,
                status: editDeliveryLocationType.status,
            })
        } else {
            reset({
                name: defaultName,
                charge: 0,
                status: 'active',
            })
        }
    }, [mode, editDeliveryLocationType, reset, open, defaultName])

    const onSubmit = async (values: DeliveryLocationTypeFormState) => {
        const data = {
            name: values.name,
            charge: DataType.number(values.charge),
            status: values.status,
        }
        if (mode === 'create') {
            await onCreate(data)
        } else if (mode === 'edit' && editDeliveryLocationType) {
            await onUpdate(editDeliveryLocationType._id, data)
        }
    }

    useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

    return (
        <Dialog open={open} onOpenChange={closeDialog}>
            <DialogContent size="sm">
                <DialogHeader>
                    <DialogTitle>
                        {mode === 'create' ? 'Add delivery area' : mode === 'edit' ? 'Edit delivery area' : 'Delivery area details'}
                    </DialogTitle>
                    <DialogDescription>
                        {mode === 'create'
                            ? 'Add an area you deliver to and its delivery charge.'
                            : mode === 'edit'
                                ? 'Update the area name, charge or status.'
                                : 'View delivery area details.'}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <TextInput
                        control={control}
                        name="name"
                        label="Name"
                        placeholder="e.g. Inside Dhaka"
                        disabled={mode === 'view' || loading}
                        errors={errors}
                        rules={fields.name.rules}
                    />

                    <TextInput
                        control={control}
                        name="charge"
                        label="Delivery charge"
                        placeholder="80"
                        type="number"
                        disabled={mode === 'view' || loading}
                        errors={errors}
                        rules={fields.charge.rules}
                    />

                    <div className="border-t border-border-subtle pt-4">
                    <SwitchInput
                        control={control}
                        name="status"
                        label="Active"
                        description={formValues.status === 'active' ? 'Customers can choose this area' : 'Hidden from customers'}
                        disabled={mode === 'view' || loading}
                        activeValue="active"
                        inactiveValue="inactive"
                        rules={fields.status.rules}
                    />
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button type="button" variant="outline" onClick={closeDialog} disabled={loading}>
                        {mode === 'view' ? 'Close' : 'Cancel'}
                    </Button>
                    {mode !== 'view' && (
                        <Button
                            type="button"
                            disabled={loading}
                            onClick={handleSubmit(onSubmit)}>
                            {loading && <Spinner />}
                            {mode === 'create' ? 'Add area' : 'Save'}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
